import React, { useState, useEffect } from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '../Icon/Icon.type';
import axios from '../../utils/axios';
import { formatCost } from '../../utils/formatCost';
import ArrowUpRightIcon from '../../assets/icons/arrow-up-right.svg';
import ArrowDownRightIcon from '../../assets/icons/arrow-down-right.svg';

interface ProjectStatisticsProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  currentMode?: 'design' | 'build' | 'cab';
}

interface ProjectTokenStats {
  total_tokens: number;
  total_cost: number;
  total_requests: number;
  input_tokens?: number;
  output_tokens?: number;
  brainstorm_tokens: number;
  brainstorm_cost: number;
  brainstorm_requests: number;
  brainstorm_input_tokens?: number;
  brainstorm_output_tokens?: number;
  development_tokens: number;
  development_cost: number;
  development_requests: number;
  development_input_tokens?: number;
  development_output_tokens?: number;
  cab_tokens?: number;
  cab_cost?: number;
  cab_requests?: number;
  cab_input_tokens?: number;
  cab_output_tokens?: number;
  last_token_activity?: string;
}

export const ProjectStatistics: React.FC<ProjectStatisticsProps> = ({
  isOpen,
  onClose,
  projectId,
  currentMode = 'build',
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenStats, setTokenStats] = useState<ProjectTokenStats | null>(null);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchAnalyticsData();
    }
  }, [isOpen, projectId]);

  const fetchAnalyticsData = async () => {
    if (!projectId) {
      setError('No project selected');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/billing/projects/${projectId}/detailed-analytics`);
      console.log('Analytics API Response:', response.data);
      
      // The API returns { token_stats: ProjectTokenStats, ... }
      if (response.data && response.data.token_stats) {
        setTokenStats(response.data.token_stats);
      } else {
        console.warn('No analytics data available');
        setTokenStats(null);
      }
    } catch (err: any) {
      console.error('Failed to fetch analytics data:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const formatTokenCount = (tokens: number): string => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  const getModeIcon = (mode: string) => {
    const isDesignMode = mode === 'brainstorm' || mode === 'design';
    const isCABMode = mode === 'cab';
    
    if (isDesignMode) {
      return (
        <div className="w-[28px] h-[28px] flex items-center justify-center">
          <Icon 
            name={IconName.Design} 
            size="xl" 
            className="text-border-brand-icn"
            inheritColor={true}
          />
        </div>
      );
    } else if (isCABMode) {
      return (
        <div className="w-[28px] h-[28px] flex items-center justify-center">
          <Icon 
            name={IconName.Document} 
            size="xl" 
            className="text-border-brand-icn"
            inheritColor={true}
          />
        </div>
      );
    } else {
      return (
        <div className="w-[28px] h-[28px] flex items-center justify-center">
          <Icon 
            name={IconName.Code} 
            size="xl" 
            className="text-border-brand-icn"
            inheritColor={true}
          />
        </div>
      );
    }
  };

  const getModeTitle = (mode: string): string => {
    if (mode === 'brainstorm' || mode === 'design') {
      return 'Prototype Mode';
    } else if (mode === 'development') {
      return 'Build Mode';
    } else if (mode === 'cab') {
      return 'CAB Mode';
    }
    return mode;
  };

  const getModeDescription = (mode: string): string => {
    if (mode === 'brainstorm' || mode === 'design') {
      return 'UI generation & iterations';
    } else if (mode === 'development') {
      return 'Code generation & debugging';
    } else if (mode === 'cab') {
      return 'Application blueprint generation';
    }
    return '';
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[470px] flex flex-col border-l border-border-secondary-alt overflow-y-auto ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Header */}
        <div className="flex px-bg-v2-3xl pt-bg-v2-3xl pb-bg-v2-xl items-start gap-bg-v2-md self-stretch">
          <div className="flex flex-col gap-bg-v2-xxs flex-1">
            <Text variant="text-lg" weight="semibold" color="primary">
              Project Statistics
            </Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200 w-8 h-8"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col px-bg-v2-xl">
          {/* Divider */}
          <div className="bg-border-secondary h-px w-full" />
          
          <div className="flex flex-col gap-[16px] pt-[16px] pb-bg-v2-xl">

          {loading && (
            <>
              {/* Total Usage Card Skeleton */}
              <div className="bg-bg-brand-primary-alt border border-utility-brand-100-alt rounded-[12px] p-[21px] flex flex-col gap-[16px]">
                <div className="h-5 skeleton-shimmer rounded w-24"></div>
                
                {/* Stats Row Skeleton */}
                <div className="flex gap-[24px] items-start justify-center">
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-16"></div>
                    <div className="h-8 skeleton-shimmer rounded w-20"></div>
                  </div>
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-16"></div>
                    <div className="h-8 skeleton-shimmer rounded w-12"></div>
                  </div>
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-20"></div>
                    <div className="h-8 skeleton-shimmer rounded w-16"></div>
                  </div>
                </div>

                {/* Input/Output Skeleton */}
                <div className="border-t border-utility-brand-100-alt pt-[17px] flex gap-[24px]">
                  <div className="flex flex-col gap-[4px] flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-12 mx-auto"></div>
                    <div className="h-7 skeleton-shimmer rounded w-16 mx-auto"></div>
                  </div>
                  <div className="flex flex-col gap-[4px] flex-1">
                    <div className="h-4 skeleton-shimmer rounded w-12 mx-auto"></div>
                    <div className="h-7 skeleton-shimmer rounded w-16 mx-auto"></div>
                  </div>
                </div>
              </div>

              {/* Mode Cards Skeleton */}
              {[1, 2, 3].map((index) => (
                <div 
                  key={index}
                  className="border border-border-secondary rounded-[12px] p-[21px] flex flex-col gap-[16px] bg-bg-primary"
                >
                  {/* Mode Header Skeleton */}
                  <div className="flex items-start justify-between">
                    <div className="flex gap-[12px] items-center">
                      <div className="skeleton-shimmer rounded-bg-v2-md w-[56px] h-[56px]"></div>
                      <div className="flex flex-col gap-[8px]">
                        <div className="h-5 skeleton-shimmer rounded w-24"></div>
                        <div className="h-4 skeleton-shimmer rounded w-32"></div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-[8px]">
                      <div className="h-5 skeleton-shimmer rounded w-16"></div>
                      <div className="h-4 skeleton-shimmer rounded w-20"></div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="bg-border-secondary h-px w-full" />

                  {/* Token Usage Skeleton */}
                  <div className="flex items-center justify-between">
                    <div className="h-4 skeleton-shimmer rounded w-20"></div>
                    <div className="h-5 skeleton-shimmer rounded w-16"></div>
                  </div>

                  {/* Divider */}
                  <div className="bg-border-secondary h-px w-full" />

                  {/* Input/Output Skeleton */}
                  <div className="flex gap-[24px]">
                    <div className="flex flex-col gap-[4px] flex-1">
                      <div className="h-4 skeleton-shimmer rounded w-12 mx-auto"></div>
                      <div className="h-7 skeleton-shimmer rounded w-16 mx-auto"></div>
                    </div>
                    <div className="flex flex-col gap-[4px] flex-1">
                      <div className="h-4 skeleton-shimmer rounded w-12 mx-auto"></div>
                      <div className="h-7 skeleton-shimmer rounded w-16 mx-auto"></div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {error && (
            <div className="bg-bg-error-primary border border-border-error rounded-bg-v2-lg p-bg-v2-lg">
              <Text variant="text-sm" color="error">
                {error}
              </Text>
            </div>
          )}

          {!loading && !error && tokenStats && (
            <>
              {/* Total Usage Card */}
              <div className="bg-bg-brand-primary-alt border border-utility-brand-100-alt rounded-[12px] p-[21px] flex flex-col gap-[16px]">
                <Text variant="text-md" weight="semibold" color="primary">
                  Total Usage
                </Text>

                {/* Stats Row */}
                <div className="flex gap-[24px] items-start justify-center">
                  {/* Total Cost */}
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                      Total Cost
                    </Text>
                    <Text variant="display-xs" weight="semibold" color="primary" align="center">
                      {formatCost(tokenStats.total_cost || 0)}
                    </Text>
                  </div>

                  {/* Requests */}
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                      Requests
                    </Text>
                    <Text variant="display-xs" weight="semibold" color="primary" align="center">
                      {tokenStats.total_requests || 0}
                    </Text>
                  </div>

                  {/* Total Tokens */}
                  <div className="flex flex-col gap-[4px] items-center flex-1">
                    <Text variant="text-sm" weight="medium" color="quaternary" align="center">
                      Total Tokens
                    </Text>
                    <Text variant="display-xs" weight="semibold" color="primary" align="center">
                      {formatTokenCount(tokenStats.total_tokens || 0)}
                    </Text>
                  </div>
                </div>

                {/* Input/Output Breakdown */}
                <div className="border-t border-utility-brand-100-alt pt-[17px] flex gap-[24px]">
                  {/* Input */}
                  <div className="flex flex-col gap-[4px] flex-1">
                    <div className="flex gap-bg-v2-xxs items-center justify-center">
                      <img src={ArrowUpRightIcon} alt="Input" className="w-[14px] h-[14px]" />
                      <Text variant="text-sm" weight="medium" color="quaternary">
                        Input
                      </Text>
                    </div>
                    <Text variant="text-xl" weight="semibold" color="primary" align="center">
                      {formatTokenCount(tokenStats.input_tokens || 0)}
                    </Text>
                  </div>

                  {/* Output */}
                  <div className="flex flex-col gap-[4px] flex-1">
                    <div className="flex gap-bg-v2-xxs items-center justify-center">
                      <img src={ArrowDownRightIcon} alt="Output" className="w-[14px] h-[14px]" />
                      <Text variant="text-sm" weight="medium" color="quaternary">
                        Output
                      </Text>
                    </div>
                    <Text variant="text-xl" weight="semibold" color="primary" align="center">
                      {formatTokenCount(tokenStats.output_tokens || 0)}
                    </Text>
                  </div>
                </div>
              </div>

              {/* Mode Breakdown Cards */}
              {[
                { mode: 'brainstorm', data: tokenStats },
                { mode: 'development', data: tokenStats },
                { mode: 'cab', data: tokenStats }
              ].map(({ mode }) => {
                const modeData = mode === 'brainstorm' ? {
                  total_cost: tokenStats.brainstorm_cost,
                  total_requests: tokenStats.brainstorm_requests,
                  total_tokens: tokenStats.brainstorm_tokens,
                  input_tokens: tokenStats.brainstorm_input_tokens,
                  output_tokens: tokenStats.brainstorm_output_tokens
                } : mode === 'cab' ? {
                  total_cost: tokenStats.cab_cost || 0,
                  total_requests: tokenStats.cab_requests || 0,
                  total_tokens: tokenStats.cab_tokens || 0,
                  input_tokens: tokenStats.cab_input_tokens || 0,
                  output_tokens: tokenStats.cab_output_tokens || 0
                } : {
                  total_cost: tokenStats.development_cost,
                  total_requests: tokenStats.development_requests,
                  total_tokens: tokenStats.development_tokens,
                  input_tokens: tokenStats.development_input_tokens,
                  output_tokens: tokenStats.development_output_tokens
                };
                
                const totalTokens = modeData.total_tokens || 0;

                return (
                  <div 
                    key={mode}
                    className="border border-border-secondary rounded-[12px] p-[21px] flex flex-col gap-[16px]"
                  >
                    {/* Mode Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex gap-[12px] items-center">
                        <div className="bg-bg-brand-primary-alt rounded-bg-v2-md w-[56px] h-[56px] flex items-center justify-center">
                          {getModeIcon(mode)}
                        </div>
                        <div className="flex flex-col">
                          <Text variant="text-md" weight="semibold" color="primary">
                            {getModeTitle(mode)}
                          </Text>
                          <Text variant="text-sm" weight="medium" color="quaternary">
                            {getModeDescription(mode)}
                          </Text>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <Text variant="text-md" weight="semibold" color="primary">
                          {formatCost(modeData.total_cost || 0)}
                        </Text>
                        <Text variant="text-sm" weight="medium" color="quaternary" align="right">
                          {modeData.total_requests || 0} requests
                        </Text>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="bg-border-secondary h-px w-full" />

                    {/* Token Usage */}
                    <div className="flex items-center justify-between">
                      <Text variant="text-sm" weight="semibold" color="primary">
                        Token Usage
                      </Text>
                      <Text variant="text-md" weight="semibold" color="primary">
                        {formatTokenCount(totalTokens)}
                      </Text>
                    </div>

                    {/* Divider */}
                    <div className="bg-border-secondary h-px w-full" />

                    {/* Input/Output Breakdown */}
                    <div className="flex gap-[24px]">
                      {/* Input */}
                      <div className="flex flex-col gap-[4px] flex-1">
                        <div className="flex gap-bg-v2-xxs items-center justify-center">
                          <img src={ArrowUpRightIcon} alt="Input" className="w-[14px] h-[14px]" />
                          <Text variant="text-sm" weight="medium" color="quaternary">
                            Input
                          </Text>
                        </div>
                        <Text variant="text-xl" weight="semibold" color="primary" align="center">
                          {formatTokenCount(modeData.input_tokens || 0)}
                        </Text>
                      </div>

                      {/* Output */}
                      <div className="flex flex-col gap-[4px] flex-1">
                        <div className="flex gap-bg-v2-xxs items-center justify-center">
                          <img src={ArrowDownRightIcon} alt="Output" className="w-[14px] h-[14px]" />
                          <Text variant="text-sm" weight="medium" color="quaternary">
                            Output
                          </Text>
                        </div>
                        <Text variant="text-xl" weight="semibold" color="primary" align="center">
                          {formatTokenCount(modeData.output_tokens || 0)}
                        </Text>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectStatistics;

